import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import {
  BadgeColor,
  BadgeType,
  BadgeComponent,
  ButtonExpand,
  ButtonVariant,
  ButtonComponent,
  Icon,
  IconComponent,
  LogoVariant,
  LogoComponent,
  Size,
  TabsComponent,
  TooltipDirective,
} from '@circlek-global/lds-components';

import { MapModalComponent } from './map-modal/map-modal.component';
import { OpeningHoursComponent } from './opening-hours/opening-hours.component';
import { StationTabContentComponent } from './station-tab-content/station-tab-content.component';

import { GtmCustomEventNameEnum } from '~components/gtm-script/gtm-custom-event-name.enum';
import { GtmCustomEventService } from '~components/gtm-script/gtm-custom-event.service';
import { StationBrand, StationFeatureCategory } from '~models/station.model';
import { TranslatePipe } from '~pipes/translate.pipe';
import { StationStatusService } from '~services/station-status.service';
import { StationsService } from '~services/station.service';
import { TranslationService } from '~services/translation.service';
import { openDirections } from '~utils/directions.utils';
import { getStationFullAddress, getStationName } from '~utils/station-name';

@Component({
  selector: 'app-station-details',
  imports: [
    BadgeComponent,
    ButtonComponent,
    IconComponent,
    LogoComponent,
    TabsComponent,
    TranslatePipe,
    OpeningHoursComponent,
    StationTabContentComponent,
    MapModalComponent,
    TooltipDirective,
  ],
  templateUrl: './station-details.component.html',
  styles: `
    /* Override LDS tabs fullWidth behavior - make tabs truly fill container width */
    /* LDS gives fullWidth tab buttons a fixed w-[240px] and the ul overflow-x-auto, */
    /* which shows a horizontal scrollbar in narrow panels */
    /* Only apply on md breakpoint and above (768px+) - below md LDS hides the ul and renders a select instead */
    @media (min-width: 768px) {
      :host ::ng-deep lds-tabs ul {
        display: flex !important;
        width: 100% !important;
        overflow-x: hidden !important;
      }
      :host ::ng-deep lds-tabs ul > li {
        flex: 1 1 0% !important;
        min-width: 0 !important;
        z-index: auto !important;
      }
      :host ::ng-deep lds-tabs ul > li > button {
        width: 100% !important;
        max-width: 100% !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
        flex-shrink: 1 !important;
      }
    }
  `,
  providers: [StationStatusService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StationDetailsComponent {
  private readonly gtmCustomEventService = inject(GtmCustomEventService);
  private readonly stationsService = inject(StationsService);
  private readonly translationService = inject(TranslationService);
  private readonly stationStatusService = inject(StationStatusService);
  readonly showBackToResults = input<boolean>(true);
  private readonly tabConfig = computed(() => {
    this.translationService.getLanguage();
    const tabs: { key: string; label: string }[] = [];

    if (this.hasServices()) {
      tabs.push({ key: 'services', label: this.translationService.translate('services') });
    }

    if (this.hasFuels()) {
      tabs.push({ key: 'fuel', label: this.translationService.translate('fuel') });
    }

    if (this.hasTruckServices()) {
      tabs.push({ key: 'truckServices', label: this.translationService.translate('truckServices') });
    }

    return tabs;
  });
  readonly backToResults = output();
  protected readonly Icon = Icon;
  protected readonly BadgeColor = BadgeColor;
  protected readonly BadgeType = BadgeType;
  protected readonly Size = Size;
  protected readonly ButtonVariant = ButtonVariant;
  protected readonly ButtonExpand = ButtonExpand;
  protected readonly LogoVariant = LogoVariant;
  protected readonly stationFeatureCategory = StationFeatureCategory;
  readonly isMapModalOpen = signal(false);
  readonly station = this.stationsService.selectedStation;
  readonly displayName = computed(() => getStationName(this.station()));
  readonly fullAddress = computed(() => {
    const station = this.station();

    if (!station) return '';

    return getStationFullAddress(station);
  });
  protected readonly statusBadgeColor = this.stationStatusService.statusBadgeColor;
  protected readonly statusLabel = this.stationStatusService.statusLabel;
  protected readonly is24Hours = computed(() => this.station()?.openingHours?.alwaysOpen === true);
  protected readonly isCoucheTardBrand = computed(() => this.station()?.brand === StationBrand.COUCHE_TARD);
  readonly regularServices = computed(() => {
    const services = this.station()?.services ?? [];

    return services.filter((s) => !s.name.includes('TRUCK') && !this.isEvService(s.name));
  });
  readonly truckServices = computed(() => {
    const services = this.station()?.services ?? [];

    return services.filter((s) => s.name.includes('TRUCK'));
  });
  readonly fuels = computed(() => this.station()?.fuels ?? []);
  readonly hasServices = computed(() => this.regularServices().length > 0);
  readonly hasFuels = computed(() => this.fuels().length > 0);
  readonly hasEvCharging = computed(() => this.station()?.services?.some((s) => this.isEvService(s.name)) ?? false);
  readonly hasTruckServices = computed(() => this.truckServices().length > 0);
  readonly visibleTabs = computed(() => this.tabConfig().map((t) => ({ label: t.label })));
  // Selected tab index lives in StationsService so it survives the desktop/mobile
  // component instance swap on breakpoint change; clamps to 0 when out of range
  readonly clampedTabIndex = computed(() => {
    const index = this.stationsService.selectedStationTabIndex();

    return index >= this.visibleTabs().length ? 0 : index;
  });
  readonly activeTabContent = computed(() => this.tabConfig()[this.clampedTabIndex()]?.key || 'services');
  // Note: Assumes 10-digit local numbers (US format). May need adjustment for EU markets
  readonly formattedPhoneNumber = computed(() => {
    const phone = this.station()?.phoneNumber;

    if (!phone) return '';

    const digits = phone.replace(/\D/g, '');

    if (phone.startsWith('+') && digits.length >= 10) {
      const countryCode = digits.slice(0, digits.length - 10);
      const rest = digits.slice(-10);

      return `+${countryCode} ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
    }

    return phone.replace(/(\d{3})(?=\d)/g, '$1 ');
  });
  readonly activeTabLabel = computed(() => this.tabConfig()[this.clampedTabIndex()].label);

  constructor() {
    effect(() => {
      this.stationStatusService.openingHours.set(this.station()?.openingHours);
    });
  }

  onBackClick(): void {
    this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.BackButton);
    this.backToResults.emit();
  }

  onCopyAddress(): void {
    const address = this.fullAddress();

    if (address) {
      navigator.clipboard.writeText(address).catch((err) => {
        console.warn('Failed to copy address:', err);
      });
    }
  }

  onDirectionsClick(): void {
    const stationData = this.station();

    if (!stationData) return;

    this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.GetDirection);

    openDirections({ latitude: stationData.latitude, longitude: stationData.longitude });
  }

  onViewMapClick(): void {
    this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.MapViewMobile);
    this.isMapModalOpen.set(true);
  }

  onMapModalClose(): void {
    this.isMapModalOpen.set(false);
  }

  onTabSelected(index: number): void {
    this.gtmCustomEventService.trackEvent(GtmCustomEventNameEnum.ServiceTabs, {
      service_tab: this.activeTabLabel(),
    });

    this.stationsService.selectedStationTabIndex.set(index);
  }

  private isEvService(name: string): boolean {
    return name === 'NA_EVCHARGING' || name.includes('CHARGER');
  }
}
